import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppModals } from "../../../app/state/modalSlice/hook/use-app-modals";
import { TEntityInfo } from "../../types/utility";
import { MenuItems } from "../types";
import { findObjectById } from "../helpers/findObjectById";
import { useAppMenus } from "../../../app/state/menuSlice/hook/use-app-menus";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { StorageScope } from "../../../services/api/instances/storageApi/types/service_types";
import { addStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import { AppRouterPaths } from "../../../services/navigation/types";

export const useSidebarObject = (
  id: string,
  menuItems: MenuItems,
  service: StorageScope,
  customEditObject?: (info: TEntityInfo | null) => void,
  customCreateObject?: (info: TEntityInfo | null) => void,
) => {
  const dispatch = useAppDispatch();
  const { handleOpenModal } = useAppModals();
  const { handleCloseMenu } = useAppMenus();
  const [folderIds, setFolderIds] = useState<string[]>([]);
  const { t } = useTranslation();

  const handleFolderClick = useCallback(
    (itemId: string) => {
      const currentObject = findObjectById(itemId, menuItems);
      if (currentObject?.children?.length) {
        setFolderIds((prev) =>
          prev.includes(itemId)
            ? prev.filter((fid) => fid !== itemId)
            : [...prev, itemId],
        );
      }
    },
    [id, folderIds, menuItems],
  );

  useEffect(() => {
    const currentObject = findObjectById(id, menuItems);
    const parentId = currentObject?.parentId;
    if (parentId) {
      setFolderIds((prev) => [...prev, parentId]);
    }
  }, [id, menuItems]);

  const handleSidebarActions = useCallback(
    (
      type: string,
      info: TEntityInfo | null,
      e?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      switch (type) {
        case "rename": {
          if (customEditObject && info?.extra !== "parent") {
            customEditObject(info);
            handleCloseMenu("sidebar_item_actions_menu", info?.extra);
            return;
          }
          handleOpenModal("object_manager", info, "edit_object");
          handleCloseMenu("sidebar_item_actions_menu", info?.extra);
          return;
        }
        case "copy": {
          handleOpenModal("object_manager", info, "create_copy_object");
          handleCloseMenu("sidebar_item_actions_menu", info?.extra);
          return;
        }
        case "delete": {
          handleOpenModal("delete_modal", info, "delete_object");
          handleCloseMenu("sidebar_item_actions_menu", info?.extra);
          return;
        }
        case "create": {
          if (customCreateObject) {
            customCreateObject(info);
            handleCloseMenu("sidebar_item_actions_menu", info?.extra);
            return;
          }
          if (service === "cases" || service === "assistants") {
            handleCloseMenu("sidebar_item_actions_menu", info?.extra);
            handleOpenModal(
              "object_manager",
              { id: info?.id ?? "", value: "child" },
              "create_object",
            );

            return;
          }
          dispatch(
            addStorageObject({
              name: t(`${service}.default_name`),
              service: service,
              objectType: "child",
              currentObjectId: info?.id,
            }),
          ).unwrap();
          handleCloseMenu("sidebar_item_actions_menu", info?.extra);
          return;
        }
        case "share": {
          handleOpenModal("share_object_modal", info);
          handleCloseMenu("sidebar_item_actions_menu", info?.extra);
          return;
        }
        case "ask_gpt": {
          handleOpenModal("ask_gpt_modal", null);
          return;
        }
        case "chat_with_agent": {
          handleOpenModal("chat_with_agent_modal", null);
          return;
        }
        case "sound_to_text": {
          handleOpenModal("sound_to_text_modal", null);
          return;
        }
        default:
          return;
      }
    },
    [service],
  );

  return { folderIds, handleFolderClick, handleSidebarActions };
};
